import React, { useEffect, useState, useCallback } from 'react';
import { clsx } from 'clsx';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { PlayfulMessage, gradientThemes } from './playfulMessages';
import { Confetti } from './Confetti';

interface PlayfulToastProps {
    message: PlayfulMessage;
    duration?: number;
    onClose: () => void;
    isVisible: boolean;
}

export const PlayfulToast: React.FC<PlayfulToastProps> = ({
    message,
    duration = 8000,
    onClose,
    isVisible,
}) => {
    const [progress, setProgress] = useState(100);
    const [isExiting, setIsExiting] = useState(false);
    const [showConfetti, setShowConfetti] = useState(false);

    // Determine if this message should trigger confetti
    const shouldShowConfetti = message.category === 'motivation' ||
                               message.text.includes('crushing') ||
                               message.text.includes('champion');

    // Handle close with exit animation
    const handleClose = useCallback(() => {
        setIsExiting(true);
        setTimeout(onClose, 400); // Wait for exit animation
    }, [onClose]);

    // Trigger confetti on mount if applicable
    useEffect(() => {
        if (isVisible && shouldShowConfetti) {
            setShowConfetti(true);
            const timeout = setTimeout(() => setShowConfetti(false), 3000);
            return () => clearTimeout(timeout);
        }
    }, [isVisible, shouldShowConfetti]);

    // Progress bar animation
    useEffect(() => {
        if (!isVisible || duration <= 0) return;

        const startTime = Date.now();
        const interval = setInterval(() => {
            const elapsed = Date.now() - startTime;
            const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
            setProgress(remaining);

            if (remaining === 0) {
                clearInterval(interval);
                handleClose();
            }
        }, 50);

        return () => clearInterval(interval);
    }, [isVisible, duration, handleClose]);

    if (!isVisible && !isExiting) return null;

    return (
        <>
            {/* Confetti effect for special messages */}
            <Confetti active={showConfetti} duration={3000} count={50} />

            <div
                className={clsx(
                    'fixed bottom-6 right-6 z-[100]',
                    'max-w-sm w-full',
                    isExiting ? 'animate-playful-exit' : 'animate-playful-entrance'
                )}
            >
            {/* Main toast container with glassmorphism */}
            <div
                className={clsx(
                    'relative overflow-hidden rounded-2xl',
                    'backdrop-blur-xl bg-gradient-to-br',
                    gradientThemes[message.theme],
                    'shadow-2xl shadow-black/20',
                    'border border-white/20'
                )}
            >
                {/* Glassmorphism overlay */}
                <div className="absolute inset-0 bg-white/10 backdrop-blur-sm" />

                {/* Content */}
                <div className="relative p-5">
                    {/* Header with emoji and close button */}
                    <div className="flex items-start justify-between gap-3">
                        {/* Animated emoji */}
                        <div className="animate-emoji-bounce text-4xl flex-shrink-0">
                            {message.emoji}
                        </div>

                        {/* Close button */}
                        <button
                            onClick={handleClose}
                            className={clsx(
                                'flex-shrink-0 p-1.5 rounded-full',
                                'bg-white/20 hover:bg-white/30',
                                'text-white/80 hover:text-white',
                                'transition-all duration-200',
                                'hover:scale-110 active:scale-95'
                            )}
                        >
                            <XMarkIcon className="w-4 h-4" />
                        </button>
                    </div>

                    {/* Message text */}
                    <p className="mt-3 text-white font-semibold text-lg leading-snug drop-shadow-sm">
                        {message.text}
                    </p>

                    {/* Category badge */}
                    <div className="mt-3 flex items-center gap-2">
                        <span className={clsx(
                            'inline-flex items-center gap-1.5 px-2.5 py-1',
                            'text-xs font-medium rounded-full',
                            'bg-white/20 text-white/90',
                            'backdrop-blur-sm'
                        )}>
                            {getCategoryLabel(message.category)}
                        </span>
                    </div>
                </div>

                {/* Progress bar */}
                <div className="h-1 bg-black/10">
                    <div
                        className="h-full bg-white/40 transition-all duration-100 ease-linear"
                        style={{ width: `${progress}%` }}
                    />
                </div>

                {/* Sparkle decorations */}
                <div className="absolute top-3 right-12 text-white/30 animate-pulse text-sm">✨</div>
                <div className="absolute bottom-8 left-4 text-white/20 animate-pulse animation-delay-200 text-xs">💫</div>
            </div>
            </div>
        </>
    );
};

function getCategoryLabel(category: string): string {
    const labels: Record<string, string> = {
        sales_wisdom: '💡 Sales Wisdom',
        coffee_break: '☕ Coffee Break',
        motivation: '🔥 Motivation',
        humor: '😄 Fun Fact',
        wellness: '🧘 Wellness',
    };
    return labels[category] || category;
}

export default PlayfulToast;
